import {createContext, useContext, useReducer, useMemo, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {fetchAllFolderAssets, fetchAllTagAssets} from '../util/FetchAssets';
import FileUtils from '../util/FileUtils';

export const FOLDER_IDX = 0;
export const TAG_IDX = 1;

const DEFAULT_SORT_BY = 'dateDes';
const DEFAULT_FILTER = 'all';

const AssetsContext = createContext();

const initialFolderState = {
    folderId: null,
    assets: [],
    loading: false,
    error: null
};

const initialTagState = {
    tag: null,
    assets: [],
    loading: false,
    error: null
};

const initialPageState = {
    typeIndex: FOLDER_IDX,
    folderId: null,
    tag: null,
    assets: [],
    totalAssets: 0,
    loading: false,
    error: null,
    page: 0,
    pageSize: 25,
    maxPage: 0,
    sortBy: DEFAULT_SORT_BY,
    filterBy: DEFAULT_FILTER,
    searchBy: '',
    dateFrom: null,
    dateTo: null,
    maintenance: {status: 'idle', assets: [], failures: []}
};

const filteredFolderObj = {
    filterId: null,
    assets: [],
    sortBy: DEFAULT_SORT_BY,
    filterBy: DEFAULT_FILTER,
    searchBy: '',
    dateFrom: null,
    dateTo: null
};
const filteredTagObj = {
    tag: null,
    assets: [],
    sortBy: DEFAULT_SORT_BY,
    filterBy: DEFAULT_FILTER,
    searchBy: '',
    dateFrom: null,
    dateTo: null
};

function getFilterHandler(filterBy) {
    switch (filterBy) {
        case 'images':
            return record => record.mimeType.startsWith('image');
        case 'videos':
            return record => record.mimeType.startsWith('video');
        case 'documents':
            return record => record.mimeType === 'application/pdf';
        case '4:1':
        case '4:3':
        case '10:1':
        case '9:16':
            return record =>
                record.mimeType.startsWith('image') &&
                FileUtils.getImageAspectRatio(record.width, record.height) === filterBy;
        default:
            return null;
    }
}

function sortRecords(records, sortBy) {
    switch (sortBy) {
        case 'dateAsc':
            records.sort((a, b) => new Date(a.created) - new Date(b.created));
            break;
        case 'dateDes':
            records.sort((a, b) => new Date(b.created) - new Date(a.created));
            break;
        case 'nameAsc':
            records.sort((a, b) => a.label.localeCompare(b.label));
            break;
        case 'nameDes':
            records.sort((a, b) => b.label.localeCompare(a.label));
            break;
        case 'sizeAsc':
            records.sort((a, b) => a.fileSize - b.fileSize);
            break;
        case 'sizeDes':
            records.sort((a, b) => b.fileSize - a.fileSize);
            break;
        case 'heightAsc':
            records.sort((a, b) => a.height - b.height);
            break;
        case 'heightDes':
            records.sort((a, b) => b.height - a.height);
            break;
        case 'widthAsc':
            records.sort((a, b) => a.width - b.width);
            break;
        case 'widthDes':
            records.sort((a, b) => b.width - a.width);
            break;
        case 'typeAsc':
            records.sort((a, b) => a.mimeType.localeCompare(b.mimeType));
            break;
        default:
            break;
    }
    return records;
}

// created: ISO-8601 string; fromMs/toMs: inclusive epoch-ms bounds (local start/end of day) or null
export function isWithinDateRange(created, fromMs, toMs) {
    if (fromMs === null && toMs === null) {
        return true;
    }
    if (!created) {
        return false;
    }
    const time = new Date(created).getTime();
    if (Number.isNaN(time)) {
        return false;
    }
    if (fromMs !== null && time < fromMs) {
        return false;
    }
    if (toMs !== null && time > toMs) {
        return false;
    }
    return true;
}

export function filterSortRecords(records, filterBy, sortBy, searchBy, dateFrom, dateTo) {
    let filteredRecords;
    const filterHandler = getFilterHandler(filterBy);
    if (filterHandler) {
        filteredRecords = records.filter(filterHandler);
    } else {
        filteredRecords = [...records];
    }
    if (searchBy && searchBy.trim().length > 0) {
        const lowerSearch = searchBy.trim().toLowerCase();
        filteredRecords = filteredRecords.filter(
            record => record.label && record.label.toLowerCase().includes(lowerSearch)
        );
    }
    if (dateFrom !== null || dateTo !== null) {
        filteredRecords = filteredRecords.filter(record => isWithinDateRange(record.created, dateFrom, dateTo));
    }
    return sortRecords(filteredRecords, sortBy);
}

function getFilteredSortedAssets(pageState, folderState, tagState, forceUpdate = false) {
    if (pageState.typeIndex === FOLDER_IDX) {
        if (
            !folderState.loading &&
            (filteredFolderObj.filterId !== pageState.folderId ||
                filteredFolderObj.sortBy !== pageState.sortBy ||
                filteredFolderObj.filterBy !== pageState.filterBy ||
                filteredFolderObj.searchBy !== pageState.searchBy ||
                filteredFolderObj.dateFrom !== pageState.dateFrom ||
                filteredFolderObj.dateTo !== pageState.dateTo ||
                forceUpdate)
        ) {
            filteredFolderObj.filterId = pageState.folderId;
            filteredFolderObj.sortBy = pageState.sortBy;
            filteredFolderObj.filterBy = pageState.filterBy;
            filteredFolderObj.searchBy = pageState.searchBy;
            filteredFolderObj.dateFrom = pageState.dateFrom;
            filteredFolderObj.dateTo = pageState.dateTo;
            filteredFolderObj.assets = filterSortRecords(
                folderState.assets,
                pageState.filterBy,
                pageState.sortBy,
                pageState.searchBy,
                pageState.dateFrom,
                pageState.dateTo
            ); // Replace with actual filtered and sorted assets
        }
        return filteredFolderObj.assets;
    }
    if (
        !tagState.loading &&
        (filteredTagObj.tag !== pageState.tag ||
            filteredTagObj.sortBy !== pageState.sortBy ||
            filteredTagObj.filterBy !== pageState.filterBy ||
            filteredTagObj.searchBy !== pageState.searchBy ||
            filteredTagObj.dateFrom !== pageState.dateFrom ||
            filteredTagObj.dateTo !== pageState.dateTo ||
            forceUpdate)
    ) {
        filteredTagObj.tag = pageState.tag;
        filteredTagObj.sortBy = pageState.sortBy;
        filteredTagObj.filterBy = pageState.filterBy;
        filteredTagObj.searchBy = pageState.searchBy;
        filteredTagObj.dateFrom = pageState.dateFrom;
        filteredTagObj.dateTo = pageState.dateTo;
        filteredTagObj.assets = filterSortRecords(
            tagState.assets,
            pageState.filterBy,
            pageState.sortBy,
            pageState.searchBy,
            pageState.dateFrom,
            pageState.dateTo
        ); // Replace with actual filtered and sorted assets
    }
    return filteredTagObj.assets;
}

function updateAssets(records, dispatch, type, changes) {
    if (changes.length === 0) {
        return records;
    }
    const updateRecords = [...records];
    const command = type.toUpperCase();
    if (command === 'UPDATE') {
        changes.forEach(asset => {
            const index = updateRecords.findIndex(record => record.id === asset.id);
            if (index !== -1) {
                updateRecords[index] = {...asset};
            }
        });
    } else if (command === 'DELETE') {
        changes.forEach(asset => {
            const index = updateRecords.findIndex(record => record.id === asset.id);
            if (index !== -1) {
                updateRecords.splice(index, 1);
            }
        });
    } else if (command === 'ADD') {
        changes.forEach(asset => {
            updateRecords.push({...asset});
        });
    } else {
        console.warn(`Unknown updateRecords type: ${type}`); // eslint-disable-line
        return records;
    }
    dispatch({type: 'UPDATE_ASSETS', assets: updateRecords});
    return updateRecords;
}

function findDuplicateAssets(assets) {
    const assetMap = new Map();
    const uniqueAssets = [];
    const duplicateAssets = [];

    // Collect assets, keeping track of unique and duplicate assets
    assets.forEach(asset => {
        // Create a unique key based on multiple attributes
        const key = JSON.stringify({
            label: asset.label,
            altText: asset.altText,
            fileSize: asset.fileSize,
            height: asset.height,
            mimeType: asset.mimeType,
            status: asset.status,
            width: asset.width
        });

        if (!assetMap.has(key)) {
            assetMap.set(key, asset);
            uniqueAssets.push(asset);
        } else {
            duplicateAssets.push(asset);
        }
    });

    // Log duplicates found
    if (duplicateAssets.length > 0) {
        // eslint-disable-next-line no-console
        console.log(
            `Found ${duplicateAssets.length} duplicate asset(s) based on label, altText, fileSize, height, mimeType, status, and width`
        );
    }

    return {uniqueAssets, duplicateAssets};
}

function fixVideoUrl(url) {
    if (url.includes('.mp4.xml')) {
        return url.replace('.mp4.xml', '.xml');
    }
    return url;
}

async function checkAssetExistence(assets) {
    // Check for asset existence using the name as URL
    const existenceChecks = assets.map(async asset => {
        if (!asset.name) {
            return {asset, exists: false};
        }

        let response;
        try {
            response = await fetch(fixVideoUrl(asset.name), {method: 'HEAD'});
            if (response.ok) {
                return {asset, exists: response.ok};
            }
            return {asset, exists: false};
        } catch (error) {
            return {asset, exists: false};
        }
    });

    const results = await Promise.all(existenceChecks);
    const existingAssets = results.filter(result => result.exists).map(result => result.asset);
    const nonExistentAssets = results.filter(result => !result.exists).map(result => result.asset);

    if (nonExistentAssets.length > 0) {
        console.log(`Found ${nonExistentAssets.length} non-existent asset(s)`); // eslint-disable-line
    }

    return {existingAssets, nonExistentAssets};
}

async function getPotentialDeleteAssets(data) {
    const potentialDeleteAssets = [];
    const {uniqueAssets, duplicateAssets} = findDuplicateAssets(data);
    const {nonExistentAssets} = await checkAssetExistence(uniqueAssets);
    potentialDeleteAssets.push(...duplicateAssets, ...nonExistentAssets);
    console.log(`Total assets marked for potential deletion: ${potentialDeleteAssets.length}`); // eslint-disable-line
    return potentialDeleteAssets;
}

export default function AssetsProvider({children}) {
    const folderReducer = (state, action) => {
        switch (action.type) {
            case 'SET_FOLDER_ID': {
                return {...state, folderId: action.folderId, loading: true, error: null, assets: []};
            }
            case 'SET_SUCCESS': {
                return {
                    ...state,
                    assets: action.assets,
                    loading: false
                };
            }
            case 'SET_ERROR': {
                return {...state, error: action.error, loading: false};
            }
            case 'UPDATE_ASSETS': {
                return {
                    ...state,
                    assets: action.assets
                };
            }
            default:
                return state;
        }
    };
    const tagReducer = (state, action) => {
        switch (action.type) {
            case 'SET_TAG': {
                return {...state, tag: action.tag, loading: true, error: null, assets: []};
            }
            case 'SET_SUCCESS': {
                return {
                    ...state,
                    assets: action.assets,
                    loading: false
                };
            }
            case 'SET_ERROR': {
                return {...state, error: action.error, loading: false};
            }
            case 'UPDATE_ASSETS': {
                return {
                    ...state,
                    assets: action.assets
                };
            }
            default:
                return state;
        }
    };
    const [folderState, folderDispatch] = useReducer(folderReducer, initialFolderState);
    const [tagState, tagDispatch] = useReducer(tagReducer, initialTagState);

    const pageReducer = (state, action) => {
        switch (action.type) {
            case 'SET_PAGE': {
                const records = getFilteredSortedAssets(state, folderState, tagState);
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const newPage = Math.max(0, Math.min(action.page, newMaxPage));
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...state,
                    assets: pagedAssets,
                    totalAssets: records.length,
                    maxPage: newMaxPage,
                    page: newPage
                };
            }
            case 'SET_PAGE_SIZE': {
                const records = getFilteredSortedAssets(state, folderState, tagState);
                const newPageSize = Math.max(1, action.pageSize);
                const newMaxPage = Math.max(0, Math.ceil(records.length / newPageSize) - 1);
                const newPage = 0;
                const startIndex = newPage * newPageSize;
                const endIndex = startIndex + newPageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {...state, pageSize: newPageSize, maxPage: newMaxPage, page: newPage, assets: pagedAssets};
            }
            case 'SET_SORT_BY': {
                const {sortBy} = action;
                const newState = {...state, sortBy};
                const records = getFilteredSortedAssets(newState, folderState, tagState);
                const newPage = 0;
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...newState,
                    maxPage: newMaxPage,
                    page: newPage,
                    totalAssets: records.length,
                    assets: pagedAssets
                };
            }
            case 'SET_FILTER_BY': {
                const {filterBy} = action;
                const newState = {...state, filterBy};
                const records = getFilteredSortedAssets(newState, folderState, tagState);
                const newPage = 0;
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...newState,
                    maxPage: newMaxPage,
                    page: newPage,
                    totalAssets: records.length,
                    assets: pagedAssets
                };
            }
            case 'SET_SEARCH_BY': {
                const {searchBy} = action;
                const newState = {...state, searchBy};
                const records = getFilteredSortedAssets(newState, folderState, tagState);
                const newPage = 0;
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...newState,
                    maxPage: newMaxPage,
                    page: newPage,
                    totalAssets: records.length,
                    assets: pagedAssets
                };
            }
            case 'SET_DATE_RANGE': {
                const {dateFrom, dateTo} = action;
                const newState = {...state, dateFrom, dateTo};
                const records = getFilteredSortedAssets(newState, folderState, tagState);
                const newPage = 0;
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...newState,
                    maxPage: newMaxPage,
                    page: newPage,
                    totalAssets: records.length,
                    assets: pagedAssets
                };
            }
            case 'SET_TYPE_INDEX': {
                const {page, typeIndex} = action;
                const newState = {...state, page, typeIndex};
                const records = getFilteredSortedAssets(newState, folderState, tagState);
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const newPage = Math.min(page, newMaxPage);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...state,
                    typeIndex,
                    page: newPage,
                    assets: pagedAssets,
                    totalAssets: records.length,
                    maxPage: newMaxPage,
                    error: typeIndex === FOLDER_IDX ? folderState.error : tagState.error,
                    loading: typeIndex === FOLDER_IDX ? folderState.loading : tagState.loading
                };
            }
            case 'SET_FOLDER_ID':
                return {...state, folderId: action.folderId, page: 0};
            case 'SET_TAG':
                return {...state, tag: action.tag, page: 0};
            case 'REFRESH_ASSETS': {
                const records = getFilteredSortedAssets(state, folderState, tagState);
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const newPage = Math.min(state.page, newMaxPage);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                return {
                    ...state,
                    error: state.typeIndex === FOLDER_IDX ? folderState.error : tagState.error,
                    loading: state.typeIndex === FOLDER_IDX ? folderState.loading : tagState.loading,
                    assets: pagedAssets,
                    totalAssets: records.length,
                    maxPage: newMaxPage,
                    page: newPage
                };
            }
            case 'COMPLETE_MAINTENANCE_CLEANUP':
            case 'UPDATE_ASSETS': {
                const isFolder = state.typeIndex === FOLDER_IDX;
                const assets = updateAssets(
                    isFolder ? folderState.assets : tagState.assets,
                    isFolder ? folderDispatch : tagDispatch,
                    action.command,
                    action.assets
                );
                const nFolderState = isFolder ? {...folderState, assets} : folderState;
                const nTagState = !isFolder ? {...tagState, assets} : tagState;
                const records = getFilteredSortedAssets(state, nFolderState, nTagState, true); // Force update
                const newMaxPage = Math.max(0, Math.ceil(records.length / state.pageSize) - 1);
                const newPage = Math.min(state.page, newMaxPage);
                const startIndex = newPage * state.pageSize;
                const endIndex = startIndex + state.pageSize;
                const pagedAssets = records.slice(startIndex, endIndex);
                const maintenance =
                    action.type !== 'COMPLETE_MAINTENANCE_CLEANUP'
                        ? state.maintenance
                        : {
                              ...state.maintenance,
                              status: 'completed',
                              assets: [],
                              failures: action.failures || []
                          };
                return {
                    ...state,
                    error: state.typeIndex === isFolder ? folderState.error : tagState.error,
                    loading: isFolder ? folderState.loading : tagState.loading,
                    assets: pagedAssets,
                    totalAssets: records.length,
                    maxPage: newMaxPage,
                    page: newPage,
                    maintenance
                };
            }
            case 'START_MAINTENANCE_CLEANUP':
                return {
                    ...state,
                    maintenance: {
                        ...state.maintenance,
                        status: 'collecting',
                        assets: [],
                        failures: []
                    }
                };
            case 'SET_MAINTENANCE_ASSETS':
                return {
                    ...state,
                    maintenance: {
                        ...state.maintenance,
                        status: 'ready',
                        assets: action.assets,
                        failures: []
                    }
                };
            case 'DELETE_MAINTENANCE_ASSETS':
                return {
                    ...state,
                    maintenance: {
                        ...state.maintenance,
                        status: 'deleting'
                    }
                };
            default:
                return state;
        }
    };

    const [pageState, pageDispatch] = useReducer(pageReducer, initialPageState);

    const folderAbortControllerRef = useRef(null);
    const tagAbortControllerRef = useRef(null);

    useEffect(() => {
        const loadData = async () => {
            if (pageState.folderId && pageState.folderId !== folderState.folderId) {
                // Cancel any previous folder fetch
                if (folderAbortControllerRef.current) {
                    folderAbortControllerRef.current.abort();
                }
                folderAbortControllerRef.current = new AbortController();
                const currentController = folderAbortControllerRef.current;

                folderDispatch({type: 'SET_FOLDER_ID', folderId: pageState.folderId});
                const data = await fetchAllFolderAssets(pageState.folderId, currentController.signal);

                // Check if this request was aborted
                if (currentController.signal.aborted) {
                    console.log(`Request for folder ${pageState.folderId} was aborted`); // eslint-disable-line
                    return;
                }

                if (data && Array.isArray(data)) {
                    folderDispatch({type: 'SET_SUCCESS', assets: data});
                } else if (data instanceof Error && data.message === 'Request aborted') {
                    // Ignore aborted requests
                } else {
                    folderDispatch({type: 'SET_ERROR', error: 'Failed to load assets'});
                }
            }
        };
        loadData();
    }, [pageState.folderId, folderState.folderId]);

    useEffect(() => {
        if (pageState.typeIndex === FOLDER_IDX) {
            pageDispatch({type: 'REFRESH_ASSETS'});
        }
    }, [folderState.assets, folderState.error, pageState.typeIndex]);

    useEffect(() => {
        const loadData = async () => {
            if (pageState.tag && pageState.tag !== tagState.tag) {
                // Cancel any previous tag fetch
                if (tagAbortControllerRef.current) {
                    tagAbortControllerRef.current.abort();
                }
                tagAbortControllerRef.current = new AbortController();
                const currentController = tagAbortControllerRef.current;

                tagDispatch({type: 'SET_TAG', tag: pageState.tag});
                const data = await fetchAllTagAssets(pageState.tag, currentController.signal);

                // Check if this request was aborted
                if (currentController.signal.aborted) {
                    console.log(`Request for tag ${pageState.tag} was aborted`); // eslint-disable-line
                    return;
                }

                if (data && Array.isArray(data)) {
                    tagDispatch({type: 'SET_SUCCESS', assets: data});
                } else if (data instanceof Error && data.message === 'Request aborted') {
                    // Ignore aborted requests
                } else {
                    tagDispatch({type: 'SET_ERROR', error: 'Failed to load assets'});
                }
            }
        };
        loadData();
    }, [pageState.tag, tagState.tag]);

    useEffect(() => {
        if (pageState.typeIndex === TAG_IDX) {
            pageDispatch({type: 'REFRESH_ASSETS'});
        }
    }, [tagState.assets, tagState.error, pageState.typeIndex]);

    useEffect(() => {
        if (!pageState.loading && pageState.maintenance.status === 'collecting') {
            (async () => {
                const potentialDeleteAssets = await getPotentialDeleteAssets(
                    pageState.typeIndex === FOLDER_IDX ? folderState.assets : tagState.assets
                );
                pageDispatch({
                    type: 'SET_MAINTENANCE_ASSETS',
                    assets: potentialDeleteAssets
                });
            })();
        }
    }, [pageState.loading, pageState.maintenance, pageState.typeIndex, folderState.assets, tagState.assets]);

    const value = useMemo(() => ({pageState, pageDispatch}), [pageState, pageDispatch]);

    return <AssetsContext.Provider value={value}>{children}</AssetsContext.Provider>;
}

AssetsProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired
};

// Custom hook to use the Assets context
export function useAssets() {
    const context = useContext(AssetsContext);
    if (context === undefined) {
        throw new Error('useAssets must be used within an AssetsProvider');
    }
    return context;
}
